import React from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LineChart,
  Line,
  BarChart,
  Bar
} from 'recharts';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Paper,
  Chip,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  CalendarToday,
  TrendingUp,
  Download,
  Visibility
} from '@mui/icons-material';

const MonthlyReport = ({ data }) => {
  const [chartType, setChartType] = React.useState('area');
  const [selectedYear, setSelectedYear] = React.useState(new Date().getFullYear());

  const handleChartTypeChange = (event, newType) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  if (!data || !data.monthly_data) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarToday />
            Monthly Performance Report
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: 200,
            color: 'text.secondary',
            flexDirection: 'column'
          }}>
            <Typography variant="body1">No monthly data available</Typography>
            <Typography variant="body2">Report will show once data is collected</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const monthlyData = data.monthly_data;
  const currentYear = data.year;

  // Calculate year-over-year metrics
  const totalShipments = monthlyData.reduce((sum, month) => sum + month.total_shipments, 0);
  const totalDelivered = monthlyData.reduce((sum, month) => sum + month.delivered, 0);
  const avgDeliveryRate = totalShipments > 0 ? ((totalDelivered / totalShipments) * 100).toFixed(1) : 0;

  // Find peak month
  const peakMonth = monthlyData.reduce((prev, current) => 
    prev.total_shipments > current.total_shipments ? prev : current
  );

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper sx={{ p: 2, maxWidth: 250 }}>
          <Typography variant="subtitle2" gutterBottom>
            {data.month} {data.year}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Shipments:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {data.total_shipments.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Delivered:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {data.delivered.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2">Rate:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {data.delivery_rate}%
              </Typography>
            </Box>
          </Box>
        </Paper>
      );
    }
    return null;
  };

  const renderChart = () => {
    const commonProps = {
      data: monthlyData,
      margin: { top: 5, right: 30, left: 20, bottom: 5 }
    };

    switch (chartType) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="month" 
              stroke="#666"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis yAxisId="left" stroke="#666" fontSize={12} />
            <YAxis 
              yAxisId="right" 
              orientation="right" 
              stroke="#666" 
              fontSize={12}
              domain={[0, 100]}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="total_shipments"
              stroke="#8884d8"
              strokeWidth={3}
              dot={{ fill: '#8884d8', strokeWidth: 2, r: 4 }}
              name="Total Shipments"
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="delivered"
              stroke="#82ca9d"
              strokeWidth={3}
              dot={{ fill: '#82ca9d', strokeWidth: 2, r: 4 }}
              name="Delivered"
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="delivery_rate"
              stroke="#ffc658"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: '#ffc658', strokeWidth: 2, r: 3 }}
              name="Delivery Rate (%)"
            />
          </LineChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="month" 
              stroke="#666"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis stroke="#666" fontSize={12} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="total_shipments" fill="#8884d8" name="Total Shipments" />
            <Bar dataKey="delivered" fill="#82ca9d" name="Delivered" />
          </BarChart>
        );

      default: // area
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="month" 
              stroke="#666"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis stroke="#666" fontSize={12} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area
              type="monotone"
              dataKey="total_shipments"
              stackId="1"
              stroke="#8884d8"
              fill="#8884d8"
              fillOpacity={0.3}
              name="Total Shipments"
            />
            <Area
              type="monotone"
              dataKey="delivered"
              stackId="2"
              stroke="#82ca9d"
              fill="#82ca9d"
              fillOpacity={0.3}
              name="Delivered"
            />
          </AreaChart>
        );
    }
  };

  // Available years for selection (can be expanded to show multiple years)
  const availableYears = [currentYear - 1, currentYear, currentYear + 1];

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarToday />
            Monthly Performance Report
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={handleYearChange}
                label="Year"
              >
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <ToggleButtonGroup
              value={chartType}
              exclusive
              onChange={handleChartTypeChange}
              size="small"
            >
              <ToggleButton value="area">Area</ToggleButton>
              <ToggleButton value="line">Line</ToggleButton>
              <ToggleButton value="bar">Bar</ToggleButton>
            </ToggleButtonGroup>

            <Button
              variant="outlined"
              size="small"
              startIcon={<Download />}
            >
              Export
            </Button>
          </Box>
        </Box>

        {/* Year Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {totalShipments.toLocaleString()}
              </Typography>
              <Typography variant="body2">
                Total Shipments {currentYear}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {totalDelivered.toLocaleString()}
              </Typography>
              <Typography variant="body2">
                Total Delivered {currentYear}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light', color: 'info.contrastText' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {avgDeliveryRate}%
              </Typography>
              <Typography variant="body2">
                Average Delivery Rate
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {peakMonth.month}
              </Typography>
              <Typography variant="body2">
                Peak Month ({peakMonth.total_shipments})
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Chart */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {currentYear} Monthly Trends
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip 
                label={`Avg: ${avgDeliveryRate}%`}
                size="small" 
                color="primary" 
                variant="outlined" 
              />
              <Chip 
                label={`Peak: ${peakMonth.month}`}
                size="small" 
                color="success" 
                variant="outlined" 
              />
            </Box>
          </Box>

          <ResponsiveContainer width="100%" height={350}>
            {renderChart()}
          </ResponsiveContainer>
        </Box>

        {/* Monthly Breakdown */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: 2,
          mt: 3
        }}>
          {monthlyData.filter(month => month.total_shipments > 0).map((month, index) => (
            <Paper 
              key={index} 
              sx={{ 
                p: 2, 
                border: month.month === peakMonth.month ? '2px solid' : '1px solid',
                borderColor: month.month === peakMonth.month ? 'primary.main' : 'divider',
                position: 'relative'
              }}
            >
              {month.month === peakMonth.month && (
                <Chip 
                  label="Peak" 
                  color="primary" 
                  size="small" 
                  sx={{ position: 'absolute', top: -8, right: 8 }} 
                />
              )}
              
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {month.month}
                <TrendingUp 
                  sx={{ 
                    fontSize: 16, 
                    color: month.delivery_rate >= 80 ? 'success.main' : 'warning.main' 
                  }} 
                />
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Shipments:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {month.total_shipments.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Delivered:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {month.delivered.toLocaleString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Rate:</Typography>
                  <Chip
                    label={`${month.delivery_rate}%`}
                    size="small"
                    color={month.delivery_rate >= 80 ? 'success' : month.delivery_rate >= 60 ? 'warning' : 'error'}
                  />
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MonthlyReport;